import React, { useEffect, useRef, useState } from 'react';
import { numberOrNull } from '../odAnalyticsUtils';

/** Six decimal places is ~11cm — the precision the telemetry actually carries. */
export const formatCoord = (value) => Number(value).toFixed(6);

/** Google Maps deep link for a point. Their documented `search` API, not a bare `?q=`. */
export const mapsUrl = (lat, lon) => `https://www.google.com/maps/search/?api=1&query=${formatCoord(lat)},${formatCoord(lon)}`;

/**
 * A location as coordinates: a Google Maps link and a copy button.
 *
 * fleetconsole's `coordLink` less its pin button — clicking anywhere on the row already flies the
 * Route Map to the point, so a dedicated button was a second way to do the same thing taking width
 * in every row of the column. The two that remain answer questions the row click does not: the
 * link answers "what is at this place", and copy exists because a coordinate's destination is
 * usually some other tool.
 *
 * The reverse-geocoded address is not dropped: it moves into the link's tooltip, where it explains
 * the numbers without costing the column the width an address needs.
 */
const OdCoordCell = (props) => {
    const { lat, lon, address } = props;

    const [copied, setCopied] = useState(false);
    const timer = useRef(null);

    // Late setState on an unmounted row is the classic table-cell leak: rows unmount on sort,
    // filter and pagination, all of which are one click away from the copy button.
    useEffect(() => () => clearTimeout(timer.current), []);

    // numberOrNull, not Number(): Number(null) is 0, which is finite, so a row the device gave no
    // position for would otherwise render a link to Null Island off the coast of Ghana.
    if (numberOrNull(lat) === null || numberOrNull(lon) === null) {
        return <span className="od-cell-muted" title={address || 'No position recorded'}>—</span>;
    }

    const text = `${formatCoord(lat)}, ${formatCoord(lon)}`;

    const copy = (event) => {
        event.stopPropagation();
        // Optional chaining, not a guard: clipboard is undefined on insecure origins, and a
        // dev server on plain http is exactly where this would otherwise throw.
        navigator.clipboard?.writeText(text);
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 900);
    };

    return (
        <span className="od-coord">
            <a
              className="od-coord-link"
              href={mapsUrl(lat, lon)}
              target="_blank"
              rel="noopener noreferrer"
              title={address ? `${address} — open in Google Maps` : 'Open in Google Maps'}
              onClick={(event) => event.stopPropagation()}
            >
                {text}
            </a>
            <button
              type="button"
              className="od-coord-btn"
              title="Copy coordinates"
              aria-label={`Copy coordinates ${text}`}
              onClick={copy}
            >
                {copied ? '✓' : '⧉'}
            </button>
        </span>
    );
};

export default OdCoordCell;
